import Service from '@ember/service';
import { service } from '@ember/service';
import type { FinancialInstitution as FinancialInstitutionModel } from 'tio-data/data/resources/financial-institution.type';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import type { RemoteEvent as RemoteEventModel } from 'tio-data/data/resources/remote-event.type';
import type {
  RemoteEventLogLevel,
  RemoteEventProviderContext,
} from 'tio-data/data/resources/remote-event.type';
import type { Store as StoreService } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';

import type SessionContextService from './session-context';

export type LogLevel = RemoteEventLogLevel;
export type ProviderComponent = 'LoginSpinwheel' | 'LoginFinicityConnectLite';

type ObservabilityEventType =
  | 'INITIALIZE'
  | 'UPLOAD_START'
  | 'UPLOAD_FAILURE'
  | 'UPLOAD_SUCCESS'
  | 'PROCESSING_SUCCESS'
  | 'PROCESSING_PARTIAL_SUCCESS'
  | 'PROCESSING_FAILURE'
  | 'DISPUTE_SUBMITTED';
interface ObservabilityEventOptions {
  eventName: ObservabilityEventType;
  component: string;
  document?: ObservableDocumentModel;
}

export default class RemoteEventLoggerService extends Service {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  async log(
    provider: 'spinwheel' | 'finicity',
    context: RemoteEventProviderContext,
    logLevel: LogLevel = 'INFO',
    component: ProviderComponent,
    institution?: FinancialInstitutionModel,
    callback?: string
  ) {
    const remoteEvent = this.store.createRecord<RemoteEventModel>('remote-event', {
      eventType: 'REMOTE_LOGGER',
      payload: {
        logLevel: logLevel,
        data: {
          event: {
            id: crypto.randomUUID(),
            institution: {
              id: institution?.id || 'unknown',
              name: institution?.name || 'unknown',
            },
            company: {
              id: this.sessionContext.currentCompany?.id || 'unknown',
              name: this.sessionContext.currentCompany?.name || 'unknown',
            },
            [provider]: {
              context,
              callback,
            },
          },
          component,
        },
      },
    });
    remoteEvent.user = this.sessionContext.user;
    return saveRecord(this.store, remoteEvent);
  }

  async logObservabilityEvent(options: ObservabilityEventOptions) {
    const { document } = options;
    const structuredContent = document?.structuredContent;
    const servicerName =
      structuredContent && 'servicer_name' in structuredContent
        ? (structuredContent.servicer_name ?? 'unknown')
        : 'unknown';
    const pseudoAccounts =
      structuredContent && 'pseudo_accounts' in structuredContent
        ? (structuredContent.pseudo_accounts?.join(',') ?? 'unknown')
        : 'unknown';

    const remoteEvent = this.store.createRecord<RemoteEventModel>('remote-event', {
      eventType: 'REMOTE_LOGGER',
      payload: {
        logLevel: 'INFO',
        data: {
          component: options.component,
          event: {
            eventName: options.eventName,
            id: crypto.randomUUID(),
            company: {
              id: this.sessionContext.currentCompany?.id || 'unknown',
              name: this.sessionContext.currentCompany?.name || 'unknown',
            },
            document: { id: document?.id || 'unknown' },
            asset: { id: document?.observableAsset?.id || 'unknown' },
            servicer_name: servicerName,
            pseudo_accounts: pseudoAccounts,
            documentType: document?.provider || 'unknown',
          },
        },
      },
    });
    remoteEvent.user = this.sessionContext.user;
    return saveRecord(this.store, remoteEvent);
  }
}
